import { mergeAttributes, Node } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    CheckCircle2,
    Link2,
    MousePointerClick,
    Square,
    SquareDashed,
    Trash2,
} from 'lucide-react'
import { useState } from 'react'

import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

import type { ButtonAttrs } from '../types'

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        emailButton: {
            insertButton: (attrs?: Partial<ButtonAttrs>) => ReturnType
        }
    }
}

const DEFAULTS: ButtonAttrs = {
    text: 'Open my order',
    url: 'https://{{ storeDomain }}/account',
    align: 'left',
    variant: 'filled',
    radius: 'smooth',
    bgColor: null,
    textColor: null,
}

const RADIUS_PX: Record<ButtonAttrs['radius'], number> = {
    sharp: 0,
    smooth: 6,
    round: 9999,
}

/**
 * Email button node — click the button to open the inspector popover
 * (align / variant / radius / colors + inline url & label editing).
 *
 * Rendered by `renderer.ts` into an email-safe <table><tr><td><a/></td></tr></table>.
 */
export const ButtonExtension = Node.create({
    name: 'emailButton',
    group: 'block',
    atom: true,
    draggable: true,

    addAttributes() {
        const make = (key: keyof ButtonAttrs) => ({
            default: DEFAULTS[key],
            parseHTML: (el: HTMLElement) =>
                el.getAttribute(`data-${kebab(key)}`) ?? DEFAULTS[key],
            renderHTML: (a: Record<string, unknown>) => {
                const val = a[key]
                if (val === null || val === undefined) return {}
                return { [`data-${kebab(key)}`]: String(val) }
            },
        })
        return {
            text: make('text'),
            url: make('url'),
            align: make('align'),
            variant: make('variant'),
            radius: make('radius'),
            bgColor: make('bgColor'),
            textColor: make('textColor'),
        }
    },

    parseHTML() {
        return [{ tag: 'div[data-type="email-button"]' }]
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-type': 'email-button' }),
        ]
    },

    addCommands() {
        return {
            insertButton:
                (attrs) =>
                ({ chain }) =>
                    chain()
                        .focus()
                        .insertContent({
                            type: this.name,
                            attrs: { ...DEFAULTS, ...attrs },
                        })
                        .run(),
        }
    },

    addNodeView() {
        return ReactNodeViewRenderer(ButtonNodeView)
    },
})

function kebab(s: string) {
    return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)
}

/* ----------------------------------------------------------------------------
   Node view
---------------------------------------------------------------------------- */

function ButtonNodeView(props: NodeViewProps) {
    const { node, updateAttributes, selected, deleteNode } = props
    const { t } = useTranslation('emails')
    const attrs = node.attrs as ButtonAttrs
    const [open, setOpen] = useState(false)

    const alignClass =
        attrs.align === 'center'
            ? 'justify-center'
            : attrs.align === 'right'
                ? 'justify-end'
                : 'justify-start'

    const isFilled = attrs.variant === 'filled'
    const bg = attrs.bgColor || '#111111'
    const fg = attrs.textColor || (isFilled ? '#ffffff' : '#111111')
    const borderColor = attrs.bgColor || '#111111'

    return (
        <NodeViewWrapper
            className={cn(
                'my-2 flex',
                alignClass,
                selected && 'outline outline-2 outline-offset-4 outline-primary/40 rounded-md',
            )}
        >
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-px"
                        style={{
                            backgroundColor: isFilled ? bg : 'transparent',
                            color: fg,
                            border: isFilled ? 'none' : `2px solid ${borderColor}`,
                            borderRadius: RADIUS_PX[attrs.radius],
                        }}
                    >
                        <span className="truncate max-w-[320px]">
                            {attrs.text || 'Button'}
                        </span>
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" side="bottom" className="w-80 space-y-3 p-3">
                    <div className="flex items-center gap-2 border-b pb-2">
                        <MousePointerClick className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium">Button</span>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t('builder.button.label')}
                        </label>
                        <Input
                            value={attrs.text}
                            onChange={(e) => updateAttributes({ text: e.target.value })}
                            placeholder={t('builder.button.labelPlaceholder')}
                            className="h-8 text-sm"
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            <Link2 className="h-3 w-3" /> URL
                        </label>
                        <Input
                            value={attrs.url}
                            onChange={(e) => updateAttributes({ url: e.target.value })}
                            placeholder={t('builder.button.urlPlaceholder')}
                            className="h-8 font-mono text-xs"
                        />
                        <p className="text-[10px] text-muted-foreground">
                            {t('builder.button.urlTip')}{' '}
                            <span className="font-mono">{'{{ checkoutUrl }}'}</span>
                        </p>
                    </div>

                    <SegmentGroup
                        label={t('builder.button.alignment')}
                        value={attrs.align}
                        options={[
                            { value: 'left', icon: AlignLeft, label: 'Left' },
                            { value: 'center', icon: AlignCenter, label: 'Center' },
                            { value: 'right', icon: AlignRight, label: 'Right' },
                        ]}
                        onChange={(v) => updateAttributes({ align: v })}
                    />

                    <SegmentGroup
                        label={t('builder.button.style')}
                        value={attrs.variant}
                        options={[
                            { value: 'filled', icon: Square, label: 'Filled' },
                            { value: 'outline', icon: SquareDashed, label: 'Outline' },
                        ]}
                        onChange={(v) => updateAttributes({ variant: v })}
                    />

                    <SegmentGroup
                        label={t('builder.button.corners')}
                        value={attrs.radius}
                        options={[
                            { value: 'sharp', label: '0' },
                            { value: 'smooth', label: '6' },
                            { value: 'round', label: 'Pill' },
                        ]}
                        onChange={(v) => updateAttributes({ radius: v })}
                    />

                    <div className="grid grid-cols-2 gap-2">
                        <ColorField
                            label={t('builder.button.background')}
                            value={attrs.bgColor || '#111111'}
                            onChange={(c) => updateAttributes({ bgColor: c })}
                            onReset={() => updateAttributes({ bgColor: null })}
                        />
                        <ColorField
                            label={t('builder.button.text')}
                            value={attrs.textColor || (isFilled ? '#ffffff' : '#111111')}
                            onChange={(c) => updateAttributes({ textColor: c })}
                            onReset={() => updateAttributes({ textColor: null })}
                        />
                    </div>

                    <div className="flex items-center justify-between border-t pt-2">
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <CheckCircle2 className="h-3 w-3" /> {t('builder.button.emailCompatible')}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false)
                                deleteNode()
                            }}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                        >
                            <Trash2 className="h-3 w-3" /> {t('builder.button.delete')}
                        </button>
                    </div>
                </PopoverContent>
            </Popover>
        </NodeViewWrapper>
    )
}

/* ----------------------------------------------------------------------------
   Tiny inline UI helpers
---------------------------------------------------------------------------- */

interface SegOption<T extends string> {
    value: T
    label: string
    icon?: React.ComponentType<{ className?: string }>
}

function SegmentGroup<T extends string>(props: {
    label: string
    value: T
    options: SegOption<T>[]
    onChange: (v: T) => void
}) {
    const { label, value, options, onChange } = props
    return (
        <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {label}
            </label>
            <div className="flex gap-1 rounded-md border bg-muted/30 p-0.5">
                {options.map((opt) => {
                    const active = opt.value === value
                    const Icon = opt.icon
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            className={cn(
                                'flex flex-1 items-center justify-center gap-1 rounded-sm px-2 py-1 text-xs transition-colors',
                                active
                                    ? 'bg-background shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {Icon && <Icon className="h-3 w-3" />}
                            {opt.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

function ColorField(props: {
    label: string
    value: string
    onChange: (v: string) => void
    onReset: () => void
}) {
    const { label, value, onChange, onReset } = props
    return (
        <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {label}
            </label>
            <div className="flex items-center gap-1 rounded-md border px-1.5 py-0.5">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent"
                />
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-6 flex-1 bg-transparent font-mono text-[11px] outline-none"
                />
                <button
                    type="button"
                    onClick={onReset}
                    className="rounded px-1 text-[10px] text-muted-foreground hover:bg-muted"
                    title="Reset"
                >
                    ×
                </button>
            </div>
        </div>
    )
}
